import { Badge, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'

import {
  NewbornInvestigationPeriod,
  NewbornInvestigationStatusItemDto
} from 'src/api/requests'
import { getFullName } from 'src/features/users/utils'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import { NewbornPeriodStateTag } from '../components/NewbornPeriodStateTag'

const periods = Object.values(NewbornInvestigationPeriod)

export const useNewbornInvestigationStatusColumns = () => {
  const { t } = useLocales()

  const columns: ColumnsType<NewbornInvestigationStatusItemDto> = [
    {
      key: 'child',
      title: t('pregnancy.newborn_investigation_status.child'),
      render: (_, { personId, firstName, lastName }) =>
        personId ? (
          <Link to={paths.persons.details(personId)}>
            {getFullName(firstName, lastName) || '-'}
          </Link>
        ) : (
          getFullName(firstName, lastName) || '-'
        )
    },
    {
      key: 'mother',
      title: t('pregnancy.newborn_investigation_status.mother'),
      render: (_, { motherPersonId, motherFirstName, motherLastName }) =>
        motherPersonId ? (
          <Link to={paths.persons.details(motherPersonId)}>
            {getFullName(motherFirstName, motherLastName) || '-'}
          </Link>
        ) : (
          '-'
        )
    },
    {
      key: 'birthDate',
      title: t('pregnancy.birth_date'),
      render: (_, { birthDate }) =>
        birthDate ? dayjs(birthDate).format(dayMonthYearFormatWithDot) : '-'
    },
    ...periods.map((period) => ({
      key: period,
      title: t(`pregnancy.newborn_period_status.${period}`),
      render: (_: unknown, record: NewbornInvestigationStatusItemDto) => (
        <NewbornPeriodStateTag
          period={record.periods?.find((item) => item.period === period)}
        />
      )
    })),
    {
      key: 'completeness',
      title: t('pregnancy.newborn_investigation_status.completeness'),
      render: (_, { isComplete, missingCount }) =>
        isComplete ? (
          <Badge
            status="success"
            text={t('pregnancy.newborn_investigation_status.complete')}
          />
        ) : (
          <Tooltip
            title={t('pregnancy.newborn_investigation_status.missing_count', {
              count: missingCount ?? 0
            })}
          >
            <span>
              <Badge
                status="error"
                text={t('pregnancy.newborn_investigation_status.incomplete')}
              />
            </span>
          </Tooltip>
        )
    }
  ]

  return { columns }
}
